/*
 * Copyright (c) 2024, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react';
import PropTypes from 'prop-types';

import {Box} from '@salesforce/retail-react-app/app/components/shared/ui';
import Swatch from '@salesforce/retail-react-app/app/components/swatch-group/swatch';

/**
 * Default Attribute Value
 * Renders the default attribute value for a simple product.
 *
 * @param {object} props
 * @param {object} props.attribute
 * @param {object} props.attributeValue
 * @param {number} props.index
 * @returns {React.ReactNode}
 */
export function DefaultAttributeValue({
  attribute,
  attributeValue,
  index,
}) {
  const {id, selectedValue} = attribute;
  const {href, name, image, value, orderable} = attributeValue;

  const hasSelection = Boolean(selectedValue?.value);
  const isSelected = selectedValue?.value === value;
  const isFirst = index === 0;

  // To mimic the behavior of a native radio input, only
  // one swatch should receive tab focus; the rest can be
  // selected using arrow keys when the swatch group has
  // focus. The focused element is the selected option or
  // the first in the group, if no option is selected.
  // This is a slight difference, for simplicity, from the
  // native element, where the first element is focused on
  // `Tab` and the _last_ element is focused on `Shift+Tab`
  const isFocusable = isSelected || (!hasSelection && isFirst);

  return (
    <Swatch
      key={value}
      disabled={!orderable}
      href={href}
      isFocusable={isFocusable}
      name={name}
      selected={isSelected}
      value={value}
      variant={id === 'color' ? 'circle' : 'square'}
    >
      {image ? (
        <Box
          backgroundColor={name.toLowerCase()}
          backgroundImage={`url(${image.disBaseLink || image.link})`}
          backgroundRepeat="no-repeat"
          backgroundSize="cover"
          height="100%"
          minWidth="32px"
          width="100%"
        />
      ) : (
        name
      )}
    </Swatch>
  );
}

DefaultAttributeValue.displayName = 'DefaultAttributeValue';

DefaultAttributeValue.propTypes = {
  attribute: PropTypes.object.isRequired,
  attributeValue: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
};
